import { cn, tv } from "@utils/tw"
import {
  ComponentProps,
  createContext,
  PointerEvent,
  PropsWithChildren,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Drawer as D } from "vaul"

export type DrawerProps = PropsWithChildren<{
  trigger?: ReactElement
  handle?: boolean
  open?: boolean
  defaultOpen?: boolean
  forceOpen?: boolean
  onOpenChange?: (open: boolean) => void
  onAnimationEnd?: (open: boolean) => void
  onOverlayPointerDown?: (e: PointerEvent<HTMLDivElement>) => void
  padded?: boolean
  dismissable?: boolean
  snapping?: boolean
  className?: string
  repositionInputs?: boolean
  modal?: boolean
  classes?: {
    overlay?: string
    content?: string
    handle?: string
    inner?: string
    scroller?: string
  }
}>

type SnapPoint =
  | {
      type: "index"
      value: number
    }
  | {
      type: "raw"
      value: string | null | number | undefined
    }

export function Drawer(props: DrawerProps) {
  const hasHandle = props.handle ?? true
  const [snapPoints, setSnaps] = useState<SnapPoint[]>([{ type: "raw", value: 1 }])
  const [snap, setSnap] = useState<SnapPoint>({ type: "index", value: 0 })
  const [elements, setElements] = useState<HTMLElement[]>([])
  const sensor = useRef<HTMLDivElement>(null!)

  useEffect(() => {
    if (elements.length === 0) {
      setSnaps([{ type: "raw", value: 1 }])
      setSnap({ type: "index", value: 0 })
      return
    }
    const update = () => {
      if (!sensor.current) return
      const sensorBounds = sensor.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const sensorTop = sensorBounds.top
      // console.log("ST", sensorTop)
      // console.log("sensor", sensorBounds.height)
      const offset = window.innerHeight - sensorBounds.height
      const snaps: SnapPoint[] = elements.map((el, i) => {
        const bounds = el.getBoundingClientRect()
        const relativeTop = bounds.top - sensorTop + bounds.height
        // console.log(
        //   "MEASURE",
        //   i,
        //   sensorTop,
        //   bounds.top,
        //   bounds.height,
        //   relativeTop,
        //   Math.round(windowHeight - relativeTop),
        // )
        return {
          type: "raw",
          value: Math.round(offset + relativeTop),
        }
      })
      snaps.sort((a, b) => {
        if (a.value === b.value) return 0
        return a.value! > b.value! ? 1 : -1
      })
      snaps.forEach((snap, i) => (snap.value += "px"))
      // console.log("SNAPS", snaps)
      snaps.push({ type: "raw", value: 1 })
      setSnap((prev) => {
        if (prev.type === "raw") return { type: "index", value: 0 }
        if (prev.type === "index" && prev.value > snaps.length) {
          return { type: "index", value: snaps.length - 1 }
        }
        return prev
      })
      // setSnaps([{ type: "raw", value: window.innerHeight - sensorBounds.height + 50 + "px" }])
      setSnaps(snaps)
    }
    const observer = new ResizeObserver((entries) => {
      update()
    })
    update()
    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [elements])

  const snapPointValues = snapPoints.map((snap) => {
    if (snap.type === "raw") {
      return snap.value!
    } else {
      return 0
    }
  })

  snapPointValues.unshift(0)

  const activeSnapValue = snap.type === "index" ? snapPoints[snap.value].value : snap.value

  // console.log({
  //   snapPointValues,
  //   activeSnapValue,
  //   snap,
  // })

  const [open, setOpen] = useState(props.open || props.forceOpen || false)
  useEffect(() => {
    setOpen(props.open || props.forceOpen || false)
  }, [props.open, props.forceOpen])
  useEffect(() => {
    props.onOpenChange?.(open)
  }, [open])

  const styles = drawerStyles({
    hasHandle: hasHandle,
    padded: props.padded ?? true,
    scrollable: !props.snapping || activeSnapValue === 1,
  })

  const scroller = useRef<HTMLDivElement>(null!)

  const snapSubscribers = useRef(new Set<(e: number | string | null) => void>())

  const ctx = useMemo((): DrawerContext => {
    return {
      isDrawer: true,
      get container() {
        return sensor.current
      },
      close: () => setOpen(false),
      maximize: () => setSnap({ type: "raw", value: 1 }),
      scrollTo: (element) => {
        // console.log("Scroll to", element)
        setSnap({ type: "raw", value: 1 })
        // console.log("S", element.offsetTop)
        scroller.current.scrollTo({ top: element.offsetTop, behavior: "smooth" })
      },
      registerSnappingElement(element: HTMLElement) {
        setElements((prev) => [...prev, element])
        return () => {
          setElements((prev) => prev.filter((el) => el !== element))
        }
      },
      subscribeToSnap(func) {
        snapSubscribers.current.add(func)
        return () => {
          snapSubscribers.current.delete(func)
        }
      },
    }
  }, [])

  return (
    <DrawerContext.Provider value={ctx}>
      <D.Root
        open={open}
        defaultOpen={props.defaultOpen}
        onOpenChange={setOpen}
        onAnimationEnd={(open) => {
          if (!open) setSnap({ type: "index", value: 0 })
          props.onAnimationEnd?.(open)
        }}
        dismissible={props.dismissable ?? true}
        scrollLockTimeout={100}
        shouldScaleBackground={false}
        snapPoints={props.snapping ? snapPointValues : undefined!}
        activeSnapPoint={props.snapping ? activeSnapValue : undefined!}
        fadeFromIndex={props.snapping ? 0 : 1}
        setBackgroundColorOnScale={false}
        repositionInputs={props.repositionInputs}
        modal={props.modal}
        setActiveSnapPoint={(snap) => {
          snapSubscribers.current.forEach((func) => {
            func(snap)
          })
          if (snapPoints.length === 0 || !props.snapping) return
          const index = snapPoints.indexOf(snap as any)
          if (snap === 0) {
            setOpen(false)
            setSnap({ type: "raw", value: 0 })
            return
          }
          if (index >= 0) {
            // console.log("setSnap", { index })
            setSnap({ type: "index", value: index })
          } else {
            // console.log("setSnap", snap)
            setSnap({ type: "raw", value: snap })
          }
        }}
        // setActiveSnapPoint={(snap) => {
        //   console.log("Set snap", snap)
        //   setSnap(snap)
        //   // if (typeof snap === "number") {
        //   //   setSnap(snapPoints?.length! > 0 ? snap : snap + 1)
        //   //   console.log("setActiveSnapPoint Number", snapPoints?.length! > 0 ? snap : snap + 1)
        //   // } else if (typeof snap === "string") {
        //   //   const index = snapPoints?.indexOf(snap) ?? 0
        //   //   setSnap(index === -1 ? 0 : index!)
        //   //   console.log("setActiveSnapPoint String", index === -1 ? 0 : index!)
        //   // }
        // }}
      >
        <D.Trigger asChild>{props.trigger}</D.Trigger>
        <D.Portal>
          {props.modal !== false && (
            <D.Overlay
              className={styles.overlay({ className: props.classes?.overlay })}
              onPointerDown={(e) => {
                props.onOverlayPointerDown?.(e)
              }}
            />
          )}
          <D.Content
            radix-drawer=""
            className={cn(styles.content(), props.className, props.classes?.content)}
            ref={sensor}
          >
            <div className={styles.scroller({ className: props.classes?.scroller })} ref={scroller}>
              <div className={styles.inner({ className: props.classes?.inner })}>
                {hasHandle && <div className={styles.handle({ className: props.classes?.handle })} />}
                {props.children}
              </div>
            </div>
          </D.Content>
        </D.Portal>
      </D.Root>
    </DrawerContext.Provider>
  )
}

Drawer.Title = D.Title
Drawer.Description = D.Description
Drawer.Close = D.Close

type Disposer = () => void

type DrawerContext = {
  registerSnappingElement: (element: HTMLElement) => Disposer
  isDrawer: boolean
  container: HTMLDivElement | null
  close: () => void
  maximize: () => void
  scrollTo: (element: HTMLElement) => void
  subscribeToSnap: (func: (e: string | number | null) => void) => () => void
}

const DrawerContext = createContext<DrawerContext>({
  registerSnappingElement: () => () => {},
  isDrawer: false,
  container: null,
  close: () => {},
  maximize: () => {},
  scrollTo: (element) => {
    document.scrollingElement?.scrollTo({ top: element.offsetTop, behavior: "smooth" })
  },
  subscribeToSnap: () => {
    return () => {}
  },
})

Drawer.SnapContainer = function DrawerSnapContainer(props: PropsWithChildren<ComponentProps<"div">>) {
  const ref = useRef<HTMLDivElement>(null!)
  const ctx = useContext(DrawerContext)

  useEffect(() => {
    return ctx.registerSnappingElement(ref.current)
  }, [])

  return <div {...props} ref={ref} />
}

export function useParentDrawer() {
  return useContext(DrawerContext)
}

const drawerStyles = tv({
  slots: {
    overlay: "fixed inset-0 z-50 bg-[rgba(0,0,0,0.8)]",
    content: [
      "fixed outline-none inset-x-0 bottom-0 z-50 flex h-auto flex-col rounded-t-[16px] shadow-lg bg-bg text-fg max-h-screen will-change-transform",
    ],
    handle:
      "pt-3 mb-1 before:content-space before:block before:bg-bg-mid before:rounded-sm before:!w-[50px] before:h-[5px] before:mx-auto",
    inner: "",
    scroller: "overflow-y-hidden",
  },
  variants: {
    hasHandle: {
      true: {
        content: "",
      },
    },
    padded: {
      true: {
        inner: "w-grid mx-auto pb-4",
      },
    },
    scrollable: {
      true: {
        scroller: "overflow-y-auto",
      },
    },
  },
  compoundVariants: [
    {
      hasHandle: true,
      padded: true,
      className: {
        inner: "pt-2",
      },
    },
    {
      hasHandle: false,
      padded: true,
      className: { inner: "pb-4" },
    },
  ],
})
